// Overview (Shop) — Tổng quan Bán hàng (31/10/2026): trên cùng vòng trạng thái đơn của khoảng đang chọn (Hôm nay / 7 ngày
// / 30 ngày / lịch), giữa là doanh thu, cuối là 5 đơn gần đây (components/overview/parts.tsx, RecentOrders.tsx). Đại lý /
// CTV ở chế độ Bán hàng cũng vào đây (đơn tự bán của họ).

import { UpgradeBanner } from '@/components/upgrade/UpgradeBanner'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAuthState } from '@/lib/auth'
import { hasAccess } from '@/lib/permissions'
import { Section } from '@/components/ui/section'
import { AnnouncementBanners } from '@/components/announce/AnnouncementBanners'
import { OrderStatusCard, OverviewHead, RevenueHero, revenuePoints, rangePeriod, rangeQuery, useOverviewRange } from '@/components/overview/parts'
import { RecentOrders } from '@/components/overview/RecentOrders'

interface RevenueReport {
  totals: {
    revenue: number
    count: number
    deliveredCount: number
    deliveringCount: number
    pendingCount: number
    returnedCount: number
  }
  daily: { date: string; revenue: number; count: number }[]
  /** Chỉ khi khoảng là một ngày: 24 giờ (giờ VN) */
  hourly?: { hour: number; revenue: number; count: number }[]
}

interface OnboardingStep {
  key: string
  label: string
  description?: string
  done: boolean
  link: string
  optional?: boolean
}

/** Bước của GET /onboarding đã chuyển sang hồ sơ bắt buộc (GET /onboarding/required). */
const REQUIRED_PROFILE_STEPS = ['best_configured', 'bank_account']

export default function OverviewPage() {
  const auth = getAuthState()
  const shop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  // Nhân viên không được xem đơn thì không có khối Đơn hàng gần đây
  const canOrders = hasAccess(shop?.role, '/orders', shop?.tenantType, shop?.staff, shop?.workModes)

  const [checklistDismissed, setChecklistDismissed] = useState(() => {
    try {
      return localStorage.getItem(`onboarding_dismissed_${auth.activeShopId}`) === 'true'
    } catch {
      return false
    }
  })

  const [range, setRange] = useOverviewRange()
  const {
    data: revenue,
    isLoading: loadingRevenue,
    isError: revenueError,
    refetch: refetchRevenue,
  } = useQuery<RevenueReport>({
    queryKey: ['reports.revenue', range.from, range.to],
    queryFn: () => get<RevenueReport>(`/reports/revenue?${rangeQuery(range)}`),
  })
  const onb = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => get<{ steps: OnboardingStep[]; allRequiredDone: boolean }>('/onboarding'),
  })

  // Kho lấy hàng và tài khoản ngân hàng đã thuộc hồ sơ bắt buộc (màn "Hoàn tất thông tin
  // tài khoản" chặn trước khi vào đây) — checklist chỉ còn các bước tuỳ chọn.
  const steps = (onb.data?.steps ?? []).filter((s) => !REQUIRED_PROFILE_STEPS.includes(s.key))
  const showChecklist = !!onb.data && steps.some((s) => !s.optional && !s.done) && !checklistDismissed
  const doneCount = steps.filter((s) => s.done).length
  // Việc còn phải làm nổi lên trên — thứ đã xong không cần tranh chỗ nữa.
  const orderedSteps = [...steps].sort((a, b) => Number(a.done) - Number(b.done))

  const t = revenue?.totals
  const statusCounts = t
    ? {
        deliveredOrders: t.deliveredCount,
        deliveringOrders: t.deliveringCount,
        returnedOrders: t.returnedCount,
        pendingOrders: t.pendingCount,
        otherOrders: Math.max(0, t.count - t.deliveredCount - t.deliveringCount - t.returnedCount - t.pendingCount),
      }
    : null

  const checklist = showChecklist && (
    <Section
      title="Bắt đầu bán hàng"
      action={
        <div className="flex items-center gap-3 text-xs">
          <span className="tabular-nums text-muted">{doneCount}/{steps.length}</span>
          <button
            type="button"
            onClick={() => {
              setChecklistDismissed(true)
              try {
                localStorage.setItem(`onboarding_dismissed_${auth.activeShopId}`, 'true')
              } catch {}
            }}
            className="motion-state text-muted hover:text-ink"
          >
            Ẩn
          </button>
        </div>
      }
    >
      <div className="px-4 pt-3">
        <div className="h-2 overflow-hidden rounded-full bg-paperSoft" role="img" aria-label={`Đã xong ${doneCount} trên ${steps.length} bước`}>
          <div className="h-full rounded-full bg-accent" style={{ width: `${steps.length ? (doneCount / steps.length) * 100 : 0}%` }} />
        </div>
      </div>
      <ul className="divide-y divide-rule">
        {orderedSteps.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-3 px-4 py-3">
            <span className={cn('min-w-0 truncate text-sm', s.done ? 'text-muted line-through' : 'text-ink')}>
              {s.label}
            </span>
            {s.done ? (
              <span className="shrink-0 text-xs text-ok">Đã xong</span>
            ) : (
              <Link to={s.link} className="motion-state shrink-0 text-xs font-medium text-accent hover:underline">
                Cài đặt
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Section>
  )

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <OverviewHead range={range} onRange={setRange} />
      <AnnouncementBanners />
      <UpgradeBanner />
      {checklist}

      {/* Một cột cả máy tính: đơn hàng → doanh thu → đơn gần đây */}
      <div className="space-y-4 mobile:space-y-3">
        <OrderStatusCard
          counts={statusCounts}
          period={rangePeriod(range)}
          loading={loadingRevenue}
          error={revenueError}
          onRetry={() => refetchRevenue()}
        />
        <RevenueHero
          label="COD dự kiến"
          total={t?.revenue}
          points={revenuePoints(range, revenue?.daily, revenue?.hourly, (d) => d.revenue, (h) => h.revenue)}
          period={rangePeriod(range)}
          hours={range.from === range.last}
          loading={loadingRevenue}
          error={revenueError}
          onRetry={() => refetchRevenue()}
          barMax={64}
        />
        {canOrders && <RecentOrders />}
      </div>
    </div>
  )
}
